import React, { useEffect, useRef, useState } from 'react';
import { ExternalLink, Loader2, CheckCircle2 } from 'lucide-react';
import {
  targetLabel, canUndoPublish, failureText, failureReasonFromChanges, formatWhen, liveUrl, resultLabel,
  textDiff, imageFileName, pluralize, websiteUpdateStatus,
  changeText, finishBlockedText, finishMode, finishRetryText, sourceLabel, FINISH_TEXT_MAX,
} from './inlineEdit';
import { absoluteSrc, attachmentUrl } from './InlineChangeSummary';
import InlineUndoButton from './InlineUndoButton';
import useInlineFinish from './useInlineFinish';

// A read-only record of one "website update" — an inline request published
// from the Visual Editor (trial feedback F). Two audiences:
//   mode 'admin'   the admin edit-request window: commit, per-change reason and
//                  file:line, Undo while it is live, and — when the publish
//                  stopped for a person — "Finish this update" (an answer per
//                  stuck change, then one Apply and publish)
//   mode 'client'  the Content Edits page: the same story without the
//                  internals (no commit, no file names)
// `siteOrigin` makes the current pictures and "Open live page" work.

const heading = 'text-[10px] font-medium text-gray-500 tracking-wider uppercase mb-1.5';

const CHIP_TONE = {
  success: 'bg-green-400/[0.08] border-green-400/30 text-green-300',
  accent: 'bg-sky-400/[0.08] border-sky-400/25 text-sky-300',
  warning: 'bg-yellow-400/[0.08] border-yellow-400/30 text-yellow-300',
  danger: 'bg-red-500/[0.08] border-red-500/30 text-red-300',
  muted: 'bg-white/[0.03] border-white/[0.08] text-gray-400',
};

// Live / Undone / Publishing… / Under review / Couldn't publish.
export function WebsiteUpdateStatusChip({ editRequest, className = '' }) {
  const st = websiteUpdateStatus(editRequest);
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium whitespace-nowrap ${CHIP_TONE[st.tone] || CHIP_TONE.muted} ${className}`}
      data-testid="website-update-status">
      {st.label}
    </span>
  );
}

const RESULT_CLS = {
  applied: 'bg-green-400/[0.08] border-green-400/30 text-green-300',
  fallback: 'bg-yellow-400/[0.08] border-yellow-400/30 text-yellow-300',
  failed: 'bg-red-500/[0.08] border-red-500/30 text-red-300',
};
const PENDING_CLS = 'bg-white/[0.04] border-white/[0.1] text-gray-400';

// When the publish went live: the API's liveAt / mergedAt when it sends one,
// else (for a request that is live now) its last update.
function liveTime(er) {
  if (er.liveAt) return er.liveAt;
  if (er.mergedAt) return er.mergedAt;
  return er.publishState === 'live' ? er.updatedAt : null;
}

// The story of the publish, oldest first: [{ key, label, at?, text?, tone }].
// A publish that stopped for a person (needs_review, failed, deploy_failed) is
// over once the team sets the request to Resolved (done by hand) or Closed
// (declined). The timeline then ends with that, instead of still promising
// that "a NexSav team member finishes the rest".
const NEEDS_PERSON = ['needs_review', 'failed', 'deploy_failed'];
export function handledByPerson(er) {
  if (!NEEDS_PERSON.includes(er?.publishState)) return null;
  if (er?.status === 'resolved') return 'resolved';
  if (er?.status === 'closed') return 'closed';
  return null;
}

export function websiteUpdateTimeline(er) {
  const changes = Array.isArray(er?.changes) ? er.changes : [];
  const state = er?.publishState || null;
  const wentLive = ['live', 'reverting', 'reverted', 'revert_failed'].includes(state);
  const handled = handledByPerson(er);
  // "Published" only once something is live; until then the first entry is the
  // moment the person pressed Publish, which is all it records.
  const items = [{ key: 'published', label: wentLive ? 'Published' : 'Publish started', at: er?.publishStartedAt || er?.createdAt, tone: 'done' }];
  if (wentLive) items.push({ key: 'live', label: 'Live', at: liveTime(er), tone: 'done' });
  if (state === 'publishing' || state === 'deploying') items.push({ key: 'publishing', label: 'Publishing…', tone: 'current' });
  if (state === 'needs_review') {
    const applied = changes.filter((c) => c?.result?.status === 'applied').length;
    const count = `${applied} of ${pluralize(changes.length, 'change')} published`;
    items.push(handled
      ? { key: 'review', label: 'Needed a person', text: `${count} automatically.`, tone: 'warn' }
      : { key: 'review', label: 'Under review', text: `${count}; a NexSav team member finishes the rest.`, tone: 'warn' });
  }
  if (state === 'failed' || state === 'deploy_failed') {
    const reason = state === 'deploy_failed' ? 'deploy_failed' : failureReasonFromChanges(changes);
    items.push({
      key: 'failed',
      label: 'Couldn’t publish',
      text: failureText(reason) || 'Something went wrong while publishing. Nothing on the website changed.',
      tone: 'bad',
    });
  }
  if (handled === 'resolved') {
    items.push({ key: 'finished', label: 'Finished by the NexSav team', at: er?.resolvedAt || er?.updatedAt, text: 'The change was made by hand.', tone: 'done' });
  }
  if (handled === 'closed') {
    items.push({ key: 'closed', label: 'Closed', at: er?.updatedAt, text: 'The NexSav team closed this without publishing it.', tone: 'muted' });
  }
  if (state === 'reverting') items.push({ key: 'undoing', label: 'Undoing…', tone: 'current' });
  if (state === 'reverted') items.push({ key: 'undone', label: 'Undone', at: er?.revertedAt, tone: 'done' });
  if (state === 'revert_failed') items.push({ key: 'undo_failed', label: 'Undo didn’t finish', text: 'The changes are still live.', tone: 'warn' });
  return items;
}

const TONE_DOT = {
  done: 'bg-green-400',
  current: 'bg-[#B0FC38] animate-pulse',
  warn: 'bg-yellow-400',
  bad: 'bg-red-400',
  muted: 'bg-gray-500',
};

function TextDiff({ before, after }) {
  const d = textDiff(before, after);
  return (
    <div className="rounded-lg bg-white/[0.02] border border-white/[0.05] px-3 py-2 text-[12px] leading-relaxed flex flex-col gap-0.5" data-testid="text-diff">
      <p className="text-gray-300 break-words">
        <span aria-hidden="true" className="inline-block w-3 text-red-300">−</span>
        <span className="sr-only">Before: </span>
        {d.prefix}{d.removed && <del className="bg-red-500/[0.18] text-red-100 rounded-sm px-0.5">{d.removed}</del>}{d.suffix}
      </p>
      <p className="text-white break-words">
        <span aria-hidden="true" className="inline-block w-3 text-green-300">+</span>
        <span className="sr-only">After: </span>
        {d.prefix}{d.added && <ins className="bg-green-400/[0.18] text-green-100 no-underline rounded-sm px-0.5">{d.added}</ins>}{d.suffix}
      </p>
    </div>
  );
}

function ImageChange({ change, siteOrigin }) {
  const beforeSrc = change.before?.src && (siteOrigin || /^https?:/i.test(change.before.src)) ? absoluteSrc(change.before.src, siteOrigin) : '';
  const afterSrc = attachmentUrl(change.after?.attachmentKey);
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        {beforeSrc
          ? <img src={beforeSrc} alt={`Previous picture: ${imageFileName(change.before?.src)}`} className="w-20 h-14 object-cover rounded border border-white/[0.08] bg-white/[0.04]" />
          : <span className="text-[11px] text-gray-500 font-mono">{imageFileName(change.before?.src)}</span>}
        <span aria-hidden="true" className="text-gray-600">→</span>
        {afterSrc
          ? <img src={afterSrc} alt={`New picture: ${change.after?.filename || ''}`} className="w-20 h-14 object-cover rounded border border-[#B0FC38]/30 bg-white/[0.04]" />
          : <span className="text-[11px] text-gray-500">New picture</span>}
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[11px]">
        <dt className="text-gray-500">Description before</dt>
        <dd className="text-gray-300 break-words">{change.before?.alt ? `“${change.before.alt}”` : '(none)'}</dd>
        <dt className="text-gray-500">New description</dt>
        <dd className="text-white break-words">
          {change.decorative ? 'Decorative picture — no description needed' : (change.after?.alt ? `“${change.after.alt}”` : '(none)')}
        </dd>
      </dl>
    </div>
  );
}

const focusRing = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B0FC38]/60';
const radioFocus = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#B0FC38]/70';
const legendCls = 'text-[12px] font-medium text-white mb-2';

function domId(...parts) {
  return parts.map((p) => String(p ?? '').replace(/[^\w-]/g, '_')).join('-');
}

// The admin's answer for one stuck change (GET …/finish `stuck` item).
function FinishDecision({ item, choice, onChoose, idBase, disabled }) {
  const mode = finishMode(item);
  const box = 'rounded-lg border border-[#B0FC38]/20 bg-[#B0FC38]/[0.04] p-3';
  if (mode === 'pin') {
    return (
      <fieldset className={box} data-testid="finish-decision" disabled={disabled}>
        <legend className={`${legendCls} float-left w-full`}>This text is in more than one place. Which one did they mean?</legend>
        <div className="clear-left flex flex-col gap-1.5">
          {item.candidates.map((c, i) => {
            const id = `${idBase}-pin-${i}`;
            // The text around this place, so the admin can tell "Kelly Wilkerson · AIS
            // Coordinator" from "D'Metriss Bannerman Holmes · AIS Coordinator".
            const detail = (item.candidateDetails || []).find((d) => d.where === c) || null;
            return (
              <label key={c} htmlFor={id} className="flex items-start gap-2 rounded-md px-2 py-1.5 text-[12px] cursor-pointer hover:bg-white/[0.03]">
                <input
                  id={id}
                  type="radio"
                  name={`${idBase}-pin`}
                  value={c}
                  checked={choice?.pin === c}
                  onChange={() => onChoose({ pin: c })}
                  className={`mt-0.5 shrink-0 ${radioFocus}`}
                />
                <span className="flex flex-col min-w-0">
                  <span className="text-gray-200">{sourceLabel(c)}</span>
                  {detail && (detail.before || detail.after) && (
                    <span className="font-mono text-[11px] text-gray-400 break-all" data-testid="finish-candidate-snippet">
                      {detail.before ? <span className="text-gray-500">…{detail.before}</span> : null}
                      <mark className="bg-transparent text-[#B0FC38] font-semibold">{changeText(item.before)}</mark>
                      {detail.after ? <span className="text-gray-500">{detail.after}…</span> : null}
                    </span>
                  )}
                  <span className="font-mono text-[11px] text-gray-600 break-all">{c}</span>
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>
    );
  }
  if (mode === 'before') {
    const beforeId = `${idBase}-before`;
    const afterId = `${idBase}-after`;
    const hintId = `${idBase}-after-hint`;
    const noSuggestion = item.suggestedAfter == null;
    const inputCls = `w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.08] text-[13px] text-white placeholder-gray-600 focus:border-[#B0FC38]/30 ${focusRing}`;
    return (
      <fieldset className={box} data-testid="finish-decision" disabled={disabled}>
        <legend className={`${legendCls} float-left w-full`}>The site no longer has this exact text.</legend>
        <div className="clear-left flex flex-col gap-1.5">
          <label htmlFor={beforeId} className="text-[11px] font-medium text-gray-400">The site says now</label>
          <input
            id={beforeId}
            type="text"
            value={choice?.beforeText ?? ''}
            onChange={(e) => onChoose({ beforeText: e.target.value })}
            autoComplete="off"
            spellCheck={false}
            className={inputCls}
          />
          <label htmlFor={afterId} className="mt-1.5 text-[11px] font-medium text-gray-400">Will become</label>
          <input
            id={afterId}
            type="text"
            value={choice?.afterText ?? changeText(item.after)}
            onChange={(e) => onChoose({ afterText: e.target.value })}
            maxLength={FINISH_TEXT_MAX}
            aria-describedby={noSuggestion ? hintId : undefined}
            autoComplete="off"
            spellCheck={false}
            className={inputCls}
          />
          {noSuggestion && (
            <p id={hintId} className="text-[12px] text-yellow-300">Check this: the site’s text changed since the edit was made.</p>
          )}
        </div>
      </fieldset>
    );
  }
  return (
    <div className={box} data-testid="finish-decision">
      <p className="text-[12px] text-gray-300 break-words">
        <span className="font-medium text-white">Try again.</span> {finishRetryText(item)}
      </p>
    </div>
  );
}

// "Finish this update": under the change list, admin only. The questions
// themselves sit on the change cards; changes the record doesn't list (none,
// normally) are asked here.
function FinishUpdate({ finish, asking, school, unmatched, headingRef, idBase }) {
  const { phase, error, notice, complete } = finish;
  if (phase === 'off' || (phase === 'loading' && !notice)) return null;
  const hintId = `${idBase}-hint`;
  const status = phase === 'publishing'
    ? 'Publishing… This usually takes a few minutes.'
    : phase === 'live' ? 'Finished. The update is live.' : '';
  const checkAgain = (
    <button type="button" onClick={finish.reload} className={`ml-1.5 underline underline-offset-2 text-gray-300 hover:text-white rounded ${focusRing}`}>
      Check again
    </button>
  );

  return (
    <section aria-labelledby={asking || phase === 'publishing' || phase === 'live' ? `${idBase}-title` : undefined}
      className="flex flex-col gap-2" data-testid="finish-update">
      {phase === 'blocked' && (
        <p className="text-[12px] text-gray-400" data-testid="finish-blocked">
          {finishBlockedText(finish.blockedReason)}
          {finish.blockedReason === 'publish_in_flight' && checkAgain}
        </p>
      )}
      {phase === 'unavailable' && (
        <p className="text-[12px] text-gray-400" data-testid="finish-blocked">
          Couldn’t check whether this update can be finished from here.
          {checkAgain}
        </p>
      )}
      {(asking || phase === 'publishing' || phase === 'live') && (
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-3 flex flex-col gap-3">
          <h3 id={`${idBase}-title`} ref={headingRef} tabIndex={-1} className="text-[10px] font-medium text-gray-500 tracking-wider uppercase focus:outline-none">
            Finish this update
          </h3>
          {asking && (
            <>
              <p className="text-[12px] text-gray-400">
                Answer for each change above, then publish the rest. What’s already live stays as it is.
              </p>
              {unmatched.map((item) => (
                <div key={item.changeId} className="flex flex-col gap-1.5">
                  <span className="text-[11px] text-gray-500">
                    {targetLabel(item.kind === 'image' ? 'img' : null)} <span className="font-mono">{item.path}</span>
                  </span>
                  <FinishDecision item={item} choice={finish.choices[item.changeId]} onChoose={(patch) => finish.choose(item.changeId, patch)}
                    idBase={domId(idBase, item.changeId)} disabled={phase === 'sending'} />
                </div>
              ))}
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={finish.submit}
                  disabled={phase === 'ready' && !complete}
                  aria-disabled={phase === 'sending' || undefined}
                  aria-label={phase === 'sending' ? `Sending the update for ${school}` : `Apply and publish this update for ${school}`}
                  aria-describedby={phase === 'ready' && !complete ? hintId : undefined}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#B0FC38] text-gray-900 text-[13px] font-semibold hover:bg-[#9BE22E] disabled:opacity-50 disabled:cursor-not-allowed aria-disabled:opacity-60 ${focusRing}`}
                >
                  {phase === 'sending' && <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />}
                  {phase === 'sending' ? 'Sending…' : 'Apply and publish'}
                </button>
                {phase === 'ready' && !complete && (
                  <span id={hintId} className="text-[12px] text-gray-500">Answer each question above first.</span>
                )}
              </div>
            </>
          )}
        </div>
      )}
      <div role="status" aria-live="polite" className={status || notice || error ? 'flex flex-col gap-1 text-[12px]' : 'sr-only'} data-testid="finish-status">
        {status && (
          <p className={phase === 'live' ? 'text-green-300' : 'text-gray-300'}>
            {phase === 'publishing' && <Loader2 className="inline w-3.5 h-3.5 mr-1.5 animate-spin" aria-hidden="true" />}
            {phase === 'live' && <CheckCircle2 className="inline w-3.5 h-3.5 mr-1.5" aria-hidden="true" />}
            {status}
          </p>
        )}
        {notice && <p className="text-yellow-300">{notice}</p>}
        {error && <p className="text-red-300">{error}</p>}
      </div>
    </section>
  );
}

// `children` (the admin's follow-up controls) render under the change list.
// `onUpdated(er)` hears each newer version of the record while an admin's
// "Finish this update" publishes (the list keeps its row in step).
export default function WebsiteUpdateRecord({ editRequest, mode = 'client', siteOrigin = null, onReverted, onUpdated, children }) {
  const [er, setEr] = useState(editRequest);
  useEffect(() => { setEr(editRequest); }, [editRequest]);
  const admin = mode === 'admin';
  // Decided from the request as opened, like Undo: a stopped publish nobody
  // has resolved or closed yet.
  const finishActive = admin && NEEDS_PERSON.includes(editRequest?.publishState) && !handledByPerson(editRequest);
  const onUpdatedRef = useRef(onUpdated);
  onUpdatedRef.current = onUpdated;
  const finish = useInlineFinish(editRequest?.id, {
    active: finishActive,
    onUpdate: (next) => { setEr((prev) => ({ ...prev, ...next })); onUpdatedRef.current?.(next); },
  });
  const finishHeadingRef = useRef(null);
  const prevFinishPhase = useRef(finish.phase);
  // The answers and the button go away once the publish starts: focus moves
  // to the block's heading instead of getting lost.
  useEffect(() => {
    if (finish.phase === 'publishing' && prevFinishPhase.current === 'sending') finishHeadingRef.current?.focus();
    prevFinishPhase.current = finish.phase;
  }, [finish.phase]);
  // A finished update that went live can be undone like any live publish;
  // once offered, Undo stays put while it runs (as for the request as opened).
  const [undoAfterFinish, setUndoAfterFinish] = useState(false);
  useEffect(() => { setUndoAfterFinish(false); }, [editRequest?.id]);
  useEffect(() => {
    if (finish.phase === 'live' && canUndoPublish(er)) setUndoAfterFinish(true);
  }, [finish.phase, er]);
  if (!er) return null;
  const changes = Array.isArray(er.changes) ? er.changes : [];
  const timeline = websiteUpdateTimeline(er);
  const firstPath = changes[0]?.path || er.pagePath || '/';
  const href = liveUrl(siteOrigin, firstPath);
  const submitter = er.submittedBy?.name || er.submittedBy?.email || 'Unknown';
  const asking = (finish.phase === 'ready' || finish.phase === 'sending') && NEEDS_PERSON.includes(er.publishState);
  const stuckById = new Map(asking ? finish.stuck.map((item) => [item.changeId, item]) : []);
  const unmatched = asking ? finish.stuck.filter((item) => !changes.some((c) => c.id === item.changeId)) : [];
  const finishIdBase = domId('finish', er.id);

  return (
    <div className="flex flex-col gap-5" data-testid="website-update-record">
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-[12px]">
        <dt className="text-gray-500">School</dt>
        <dd className="text-gray-200">{er.accountName || '—'}</dd>
        <dt className="text-gray-500">Submitted by</dt>
        <dd className="text-gray-200">
          {submitter}
          {admin && er.submittedBy?.email && er.submittedBy?.name && (
            <a href={`mailto:${er.submittedBy.email}`} className="ml-1.5 text-gray-400 hover:text-white">{er.submittedBy.email}</a>
          )}
        </dd>
        <dt className="text-gray-500">When</dt>
        <dd className="text-gray-200">{formatWhen(er.publishStartedAt || er.createdAt) || '—'}</dd>
        {admin && (
          <>
            <dt className="text-gray-500">Commit</dt>
            <dd className="text-gray-200 font-mono" data-testid="website-update-commit">{er.mergeCommitSha || '—'}</dd>
          </>
        )}
      </dl>

      <section aria-label="Status">
        <div className={heading}>Status</div>
        <ol className="flex flex-col gap-2" data-testid="website-update-timeline">
          {timeline.map((t) => (
            <li key={t.key} className="flex items-start gap-2.5 text-[12px]">
              <span aria-hidden="true" className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${TONE_DOT[t.tone]}`} />
              <span className="flex flex-col">
                <span className="text-white">
                  {t.label}
                  {t.at && <span className="text-gray-400"> · {formatWhen(t.at)}</span>}
                </span>
                {t.text && <span className={t.tone === 'bad' ? 'text-red-300' : 'text-gray-400'}>{t.text}</span>}
              </span>
            </li>
          ))}
        </ol>
      </section>

      {changes.length > 0 && (
        <section aria-label="Changes">
          <div className={heading}>{pluralize(changes.length, 'change')}</div>
          <ol className="flex flex-col gap-3">
            {changes.map((c) => {
              const status = c.result?.status;
              return (
                <li key={c.id} className="rounded-xl bg-white/[0.02] border border-white/[0.06] p-3 flex flex-col gap-2" data-testid="website-update-change">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[13px] font-medium text-white">{targetLabel(c.kind === 'image' ? 'img' : c.fingerprint?.tag)}</span>
                    <span className="text-[11px] font-mono text-gray-500">{c.path}</span>
                    <span className={`ml-auto inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-medium ${RESULT_CLS[status] || PENDING_CLS}`}>
                      {resultLabel(status)}
                    </span>
                  </div>
                  {c.kind === 'image' ? <ImageChange change={c} siteOrigin={siteOrigin} /> : <TextDiff before={c.before?.text} after={c.after?.text} />}
                  {admin && (c.result?.reason || c.result?.file) && (
                    <div className="text-[11px] text-gray-500" data-testid="website-update-result-detail">
                      {c.result?.reason && <span>Reason: {c.result.reason}</span>}
                      {c.result?.reason && c.result?.file && <span> · </span>}
                      {c.result?.file && <span className="font-mono">{c.result.file}{c.result.line ? `:${c.result.line}` : ''}</span>}
                    </div>
                  )}
                  {stuckById.has(c.id) && (
                    <FinishDecision item={stuckById.get(c.id)} choice={finish.choices[c.id]} onChoose={(patch) => finish.choose(c.id, patch)}
                      idBase={domId(finishIdBase, c.id)} disabled={finish.phase === 'sending'} />
                  )}
                </li>
              );
            })}
          </ol>
        </section>
      )}

      {admin && (
        <FinishUpdate finish={finish} asking={asking} school={er.accountName || 'this school'} unmatched={unmatched}
          headingRef={finishHeadingRef} idBase={finishIdBase} />
      )}

      {children}

      <div className="flex flex-wrap items-center gap-3">
        {href && (
          <a href={href} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/[0.12] text-[12px] text-white hover:border-white/[0.25] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B0FC38]/60">
            Open live page <ExternalLink className="w-3 h-3" aria-hidden="true" /><span className="sr-only">(opens in a new tab)</span>
          </a>
        )}
        {/* Decided from the request as opened, so the Undo control (and its
            status line) stays put while the undo runs and after it lands. */}
        {admin && (canUndoPublish(editRequest) || undoAfterFinish) && (
          <InlineUndoButton requestId={er.id} onReverted={(next) => { setEr((prev) => ({ ...prev, ...next })); onReverted?.(next); }} />
        )}
      </div>
    </div>
  );
}
